import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  Mail,
  Paperclip,
  Send,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { DEPARTMENTS, DEPARTMENT_BY_VALUE, type Department } from '@/lib/departments'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

interface Attachment {
  id: string
  filename: string
  contentType: string | null
  sizeBytes: number | null
}

interface EmailRow {
  id: string
  fromEmail: string
  fromName: string | null
  subject: string
  textBody: string | null
  htmlBody: string | null
  receivedAt: string
  readAt: string | null
  status: string
  classification: Record<string, unknown> | null
  assignedDepartment: Department | null
  assignedToUserId: string | null
  forwardedTo: string | null
  forwardedAt: string | null
  attachments: Attachment[]
}

interface TourMailbox {
  id: string
  tourId: string
  token: string
  alias: string | null
  isActive: boolean
  tokenAddress: string
  aliasAddress: string | null
}

export function TourMailboxPage() {
  const { tourId } = useParams<{ tourId: string }>()
  const qc = useQueryClient()
  const artistId = useAuthStore((s) => s.user?.artistId)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editingAlias, setEditingAlias] = useState(false)
  const [aliasInput, setAliasInput] = useState('')

  const mailboxQ = useQuery<TourMailbox, ApiClientError>({
    queryKey: ['tour-mailbox', tourId],
    queryFn: () => artistFetch<TourMailbox>(`/tours/${tourId}/mailbox`),
    enabled: !!tourId,
  })
  const emailsQ = useQuery<EmailRow[], ApiClientError>({
    queryKey: ['tour-mail', tourId],
    queryFn: () => artistFetch<EmailRow[]>(`/tours/${tourId}/mailbox/emails`),
    enabled: !!tourId,
  })

  async function saveAlias() {
    try {
      await artistFetch(`/tours/${tourId}/mailbox`, {
        method: 'PATCH',
        body: { alias: aliasInput.trim() || null },
      })
      qc.invalidateQueries({ queryKey: ['tour-mailbox', tourId] })
      setEditingAlias(false)
      setAliasInput('')
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Failed to save alias')
    }
  }

  async function markRead(emailId: string, asRead: boolean) {
    try {
      await artistFetch(`/inbound/${emailId}`, {
        method: 'PATCH',
        body: { readAt: asRead ? new Date().toISOString() : null },
      })
      qc.invalidateQueries({ queryKey: ['tour-mail', tourId] })
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Failed to update')
    }
  }

  async function assignDepartment(emailId: string, dept: Department | null) {
    try {
      await artistFetch(`/inbound/${emailId}`, {
        method: 'PATCH',
        body: { assignedDepartment: dept },
      })
      qc.invalidateQueries({ queryKey: ['tour-mail', tourId] })
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Failed to assign')
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).catch(() => undefined)
  }

  return (
    <div>
      <Link
        to="/tours"
        className="mb-3 inline-flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Tours
      </Link>

      <PageHeader
        title="Tour mailbox"
        subtitle="Anything forwarded to this tour's address lands here, gets AI-classified, and routes through your department cascade."
      />

      <AsyncBoundary isLoading={mailboxQ.isLoading} error={mailboxQ.error}>
        {mailboxQ.data && (
          <section className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-4">
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
                  Token address
                </div>
                <div className="flex items-center gap-2 font-mono text-sm">
                  {mailboxQ.data.tokenAddress}
                  <button
                    type="button"
                    onClick={() => copyToClipboard(mailboxQ.data!.tokenAddress)}
                    className="text-ovation-muted hover:text-ovation-ink"
                    title="Copy"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-ovation-muted">
                  Always live. Use this for booking forms and machine senders.
                </p>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
                  Vanity alias
                </div>
                {editingAlias ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={aliasInput}
                      onChange={(e) => setAliasInput(e.target.value.toLowerCase())}
                      placeholder="e.g. spring-2026"
                      className="rounded-md border border-ovation-ink/15 px-2 py-1 text-sm"
                    />
                    <button
                      type="button"
                      onClick={saveAlias}
                      className="rounded-md bg-ovation-ink px-2 py-1 text-xs text-ovation-paper"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingAlias(false)
                        setAliasInput('')
                      }}
                      className="text-xs text-ovation-muted hover:text-ovation-ink"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {mailboxQ.data.aliasAddress ? (
                      <>
                        <span className="font-mono text-sm">{mailboxQ.data.aliasAddress}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(mailboxQ.data!.aliasAddress!)}
                          className="text-ovation-muted hover:text-ovation-ink"
                          title="Copy"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <span className="italic text-sm text-ovation-muted">No alias set</span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingAlias(true)
                        setAliasInput(mailboxQ.data?.alias ?? '')
                      }}
                      className="ml-auto text-xs text-ovation-muted hover:text-ovation-ink"
                    >
                      {mailboxQ.data.alias ? 'Rename' : 'Set alias'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </AsyncBoundary>

      <AsyncBoundary isLoading={emailsQ.isLoading} error={emailsQ.error}>
        <div className="rounded-lg border border-ovation-ink/10 bg-white">
          {emailsQ.data && emailsQ.data.length === 0 ? (
            <div className="p-8 text-center text-sm text-ovation-muted">
              <Mail className="mx-auto mb-2 h-6 w-6 opacity-50" />
              No mail yet. Forward emails to the address above and they'll appear here.
            </div>
          ) : (
            <ul className="divide-y divide-ovation-ink/10">
              {(emailsQ.data ?? []).map((email) => {
                const isOpen = expandedId === email.id
                const unread = !email.readAt
                return (
                  <li key={email.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedId(isOpen ? null : email.id)
                        if (!isOpen && unread) markRead(email.id, true)
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-ovation-ink/5 ${
                        unread ? 'bg-ovation-ink/[0.02]' : ''
                      }`}
                    >
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${
                          unread ? 'bg-blue-500' : 'bg-transparent'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-sm ${unread ? 'font-semibold' : ''}`}>
                          {email.fromName ?? email.fromEmail}
                        </div>
                        <div className="truncate text-xs text-ovation-muted">
                          {email.subject}
                        </div>
                      </div>
                      {email.attachments.length > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-ovation-muted">
                          <Paperclip className="h-3 w-3" />
                          {email.attachments.length}
                        </span>
                      )}
                      {email.assignedDepartment && (
                        <span className="rounded bg-ovation-ink/5 px-1.5 py-0.5 text-[10px]">
                          {DEPARTMENT_BY_VALUE[email.assignedDepartment]?.label ?? email.assignedDepartment}
                        </span>
                      )}
                      <span className="shrink-0 text-[10px] text-ovation-muted">
                        {new Date(email.receivedAt).toLocaleDateString()}
                      </span>
                      <ChevronDown
                        className={`h-3 w-3 shrink-0 text-ovation-muted transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {isOpen && (
                      <div className="border-t border-ovation-ink/10 bg-ovation-paper px-4 py-3">
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-ovation-muted">
                          <span>
                            <strong>From:</strong> {email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail}
                          </span>
                          <span className="text-ovation-ink/20">·</span>
                          <span>{new Date(email.receivedAt).toLocaleString()}</span>
                        </div>

                        {/* Body */}
                        {email.htmlBody ? (
                          <iframe
                            sandbox=""
                            srcDoc={email.htmlBody}
                            className="h-72 w-full rounded border border-ovation-ink/10 bg-white"
                            title="Email body"
                          />
                        ) : (
                          <pre className="whitespace-pre-wrap rounded border border-ovation-ink/10 bg-white p-3 text-xs">
                            {email.textBody ?? '(no body)'}
                          </pre>
                        )}

                        {/* Attachments */}
                        {email.attachments.length > 0 && (
                          <div className="mt-3">
                            <div className="mb-1 text-[10px] uppercase tracking-wider text-ovation-muted">
                              Attachments
                            </div>
                            <ul className="space-y-1">
                              {email.attachments.map((a) => (
                                <li key={a.id}>
                                  <a
                                    href={`${API_BASE_URL}/artists/${artistId}/inbound/${email.id}/attachments/${a.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-xs text-blue-700 hover:underline"
                                  >
                                    <Paperclip className="h-3 w-3" />
                                    {a.filename}
                                    {a.sizeBytes && (
                                      <span className="text-ovation-muted">
                                        ({Math.ceil(a.sizeBytes / 1024)} KB)
                                      </span>
                                    )}
                                  </a>
                                </li>
                              ))}
                            </ul>
                            <p className="mt-1 text-[10px] text-ovation-muted">
                              Link target is the API attachment proxy — it 302-redirects to a 1-hour presigned S3 URL.
                            </p>
                          </div>
                        )}

                        {/* Triage actions */}
                        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-ovation-ink/10 pt-3">
                          <div>
                            <label className="block text-[10px] uppercase tracking-wider text-ovation-muted">
                              Department
                            </label>
                            <select
                              value={email.assignedDepartment ?? ''}
                              onChange={(e) =>
                                assignDepartment(
                                  email.id,
                                  (e.target.value || null) as Department | null,
                                )
                              }
                              className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
                            >
                              <option value="">— unassigned —</option>
                              {DEPARTMENTS.map((d) => (
                                <option key={d.value} value={d.value}>
                                  {d.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <button
                            type="button"
                            onClick={() => markRead(email.id, !!email.readAt ? false : true)}
                            className="inline-flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs hover:bg-white"
                          >
                            {email.readAt ? (
                              <>
                                <Send className="h-3 w-3" />
                                Mark unread
                              </>
                            ) : (
                              <>
                                <Check className="h-3 w-3" />
                                Mark read
                              </>
                            )}
                          </button>

                          {email.forwardedTo && (
                            <div className="ml-auto text-[10px] text-ovation-muted">
                              Forwarded to {email.forwardedTo}{' '}
                              {email.forwardedAt &&
                                `at ${new Date(email.forwardedAt).toLocaleString()}`}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </AsyncBoundary>
    </div>
  )
}
